import {
  TableContainer,
  TableRow,
} from "@/components/report/disease_risk/disease_risk_v1/medical_facility_report/commons";
import { ReportTableContainer } from "@/sleep_compass_lite/components/target/ReportTableContainer";
import { PX4, PX6 } from "@/sleep_compass_lite/components/target/StackStyles";
import { StickyStack } from "@/sleep_compass_lite/components/target/StickyStack";
import { LiteReportHeaderProps } from "@/sleep_compass_lite/components/target/report/LiteReportMenuContext";
import { reportPath } from "@/sleep_compass_lite/domain_models/target/ReportPath";
import { useTargetAxios as useAxios } from "@/sleep_compass_lite/use_cases/target/common/UseTargetAxios";
import {
  Button,
  ButtonProps,
  List,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ReportPageStepper } from ".";

/**
 * 睡眠の疾患APIの返り値
 */
interface SleepDisorderInfoOfAPI {
  /** レポートのバージョン */
  report_version: string;
  /** 睡眠時無呼吸症候群リスクの根拠 */
  SAS_basis: {
    loud_snoring: BasisItemOfAPI;
    sleepy_daytime: BasisItemOfAPI;
    breathing_stopped: BasisItemOfAPI;
    hypertension: BasisItemOfAPI;
  } | null;
  /** 不眠症リスクの根拠 */
  insomnia_basis: {
    ais_sleep_latency: BasisItemOfAPI;
    ais_awakening: BasisItemOfAPI;
    ais_wakeup_earlier: BasisItemOfAPI;
    ais_total_sleep_quantity: BasisItemOfAPI;
    ais_total_sleep_quality: BasisItemOfAPI;
    ais_satisfied_daily: BasisItemOfAPI;
    ais_daily_activity: BasisItemOfAPI;
    ais_daily_sleepy_daytime: BasisItemOfAPI;
  } | null;
}

interface BasisItemOfAPI {
  caption: string;
  is_bad: boolean;
}

/**
 * 睡眠の疾患APIの返り値を変換したもの
 */
interface SleepDisorderInfo {
  reportVersion: string;
  sasBasis: {
    loudSnoring: BasisItem;
    sleepyDaytime: BasisItem;
    breathingStopped: BasisItem;
    hypertension: BasisItem;
  };
  insomniaBasis: {
    aisSleepLatency: BasisItem;
    aisAwakening: BasisItem;
    aisWakeupEarlier: BasisItem;
    aisTotalSleepQuantity: BasisItem;
    aisTotalSleepQuality: BasisItem;
    aisSatisfiedDaily: BasisItem;
    aisDailyActivity: BasisItem;
    aisDailySleepyDaytime: BasisItem;
  };
}

interface BasisItem {
  caption: string;
  isBad: boolean;
}

/**
 * 睡眠の疾患の情報をサーバーから取得
 */
function useSleepDisorderInfo({ surveyInfoUid }: { surveyInfoUid?: string }) {
  const axios = useAxios();
  const [sleepDisorderInfo, setSleepDisorderInfo] =
    useState<SleepDisorderInfo>();
  useEffect(() => {
    (async () => {
      if (surveyInfoUid == null) return;
      const response: AxiosResponse<SleepDisorderInfoOfAPI> = await axios.get(
        `/api/lite/report/${surveyInfoUid}/sleep_disorder/`
      );
      setSleepDisorderInfo({
        reportVersion: response.data.report_version,
        sasBasis: {
          loudSnoring: {
            caption: response.data.SAS_basis?.loud_snoring.caption ?? "-",
            isBad: response.data.SAS_basis?.loud_snoring.is_bad ?? false,
          },
          sleepyDaytime: {
            caption: response.data.SAS_basis?.sleepy_daytime.caption ?? "-",
            isBad: response.data.SAS_basis?.sleepy_daytime.is_bad ?? false,
          },
          breathingStopped: {
            caption: response.data.SAS_basis?.breathing_stopped.caption ?? "-",
            isBad: response.data.SAS_basis?.breathing_stopped.is_bad ?? false,
          },
          hypertension: {
            caption: response.data.SAS_basis?.hypertension.caption ?? "-",
            isBad: response.data.SAS_basis?.hypertension.is_bad ?? false,
          },
        },
        insomniaBasis: {
          aisSleepLatency: {
            caption:
              response.data.insomnia_basis?.ais_sleep_latency.caption ?? "-",
            isBad:
              response.data.insomnia_basis?.ais_sleep_latency.is_bad ?? false,
          },
          aisAwakening: {
            caption: response.data.insomnia_basis?.ais_awakening.caption ?? "-",
            isBad: response.data.insomnia_basis?.ais_awakening.is_bad ?? false,
          },
          aisWakeupEarlier: {
            caption:
              response.data.insomnia_basis?.ais_wakeup_earlier.caption ?? "-",
            isBad:
              response.data.insomnia_basis?.ais_wakeup_earlier.is_bad ?? false,
          },
          aisTotalSleepQuantity: {
            caption:
              response.data.insomnia_basis?.ais_total_sleep_quantity.caption ??
              "-",
            isBad:
              response.data.insomnia_basis?.ais_total_sleep_quantity.is_bad ??
              false,
          },
          aisTotalSleepQuality: {
            caption:
              response.data.insomnia_basis?.ais_total_sleep_quality.caption ??
              "-",
            isBad:
              response.data.insomnia_basis?.ais_total_sleep_quality.is_bad ??
              false,
          },
          aisSatisfiedDaily: {
            caption:
              response.data.insomnia_basis?.ais_satisfied_daily.caption ?? "-",
            isBad:
              response.data.insomnia_basis?.ais_satisfied_daily.is_bad ?? false,
          },
          aisDailyActivity: {
            caption:
              response.data.insomnia_basis?.ais_daily_activity.caption ?? "-",
            isBad:
              response.data.insomnia_basis?.ais_daily_activity.is_bad ?? false,
          },
          aisDailySleepyDaytime: {
            caption:
              response.data.insomnia_basis?.ais_daily_sleepy_daytime.caption ??
              "-",
            isBad:
              response.data.insomnia_basis?.ais_daily_sleepy_daytime.is_bad ??
              false,
          },
        },
      });
    })();
  }, [axios, surveyInfoUid]);
  return sleepDisorderInfo;
}

/**
 * 睡眠の疾患ページ
 */
export function SleepDisorder({
  setLiteReportHeaderContext,
}: LiteReportHeaderProps) {
  const { surveyInfoUid } = useParams();
  const sleepDisorderInfo = useSleepDisorderInfo({ surveyInfoUid });
  const navigate = useNavigate();

  const [isNextButtonFloating, setIsNextButtonFloating] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    // メニューバーの制御
    setLiteReportHeaderContext({
      title: "睡眠の疾患",
      onClickBack() {
        if (surveyInfoUid == null) return;
        navigate(
          reportPath.getFullPath("DaytimeCondition", {
            ":surveyInfoUid": surveyInfoUid,
          })
        );
      },
    });
  }, [navigate, setLiteReportHeaderContext, surveyInfoUid]);

  const nextButtonSX: ButtonProps["sx"] = { my: 6 };
  if (isNextButtonFloating === false) {
    // ボタンの影は、フローティングしていないときは非表示(フローティングしているときは表示)
    nextButtonSX["boxShadow"] = "none";
  }

  if (surveyInfoUid == null || sleepDisorderInfo == null) return <></>;
  return (
    <Stack sx={{ backgroundColor: "background.default", pb: 14 }}>
      <Stack gap={10} sx={{ px: PX6, pt: 6, pb: 10, backgroundColor: "white" }}>
        <ReportPageStepper
          version={sleepDisorderInfo.reportVersion}
          stepName="SleepDisorder"
        />
        <Stack sx={{ gap: 6 }}>
          <Typography variant="h5">
            代表的な睡眠疾患と、よく見られる自覚症状
          </Typography>
          <Typography variant="body2">
            睡眠に関連する代表的な疾患と、それに伴う主な自覚症状、あなたの回答結果をご紹介します。
            <br />
            ※本項目の内容は今回の睡眠評価には用いておらず、結果にも影響していません。
          </Typography>
          <Caution />
        </Stack>
      </Stack>
      <Stack gap={4} sx={{ mt: 10 }}>
        <SASSection
          loudSnoring={sleepDisorderInfo.sasBasis.loudSnoring}
          sleepyDaytime={sleepDisorderInfo.sasBasis.sleepyDaytime}
          breathingStopped={sleepDisorderInfo.sasBasis.breathingStopped}
          hypertension={sleepDisorderInfo.sasBasis.hypertension}
        />
        <InsomniaSection
          aisSleepLatency={sleepDisorderInfo.insomniaBasis.aisSleepLatency}
          aisAwakening={sleepDisorderInfo.insomniaBasis.aisAwakening}
          aisWakeupEarlier={sleepDisorderInfo.insomniaBasis.aisWakeupEarlier}
          aisTotalSleepQuantity={
            sleepDisorderInfo.insomniaBasis.aisTotalSleepQuantity
          }
          aisTotalSleepQuality={
            sleepDisorderInfo.insomniaBasis.aisTotalSleepQuality
          }
          aisSatisfiedDaily={sleepDisorderInfo.insomniaBasis.aisSatisfiedDaily}
          aisDailyActivity={sleepDisorderInfo.insomniaBasis.aisDailyActivity}
          aisDailySleepyDaytime={
            sleepDisorderInfo.insomniaBasis.aisDailySleepyDaytime
          }
        />
      </Stack>
      <StickyStack
        sx={{
          px: PX4,
          background:
            "linear-gradient(180deg, rgba(246, 245, 244, 0.00) 0%, #F6F5F4 100%);",
        }}
        onChangeFloatingState={(isFloating) =>
          setIsNextButtonFloating(isFloating)
        }
      >
        <Button
          variant="contained"
          sx={nextButtonSX}
          component={Link}
          to={reportPath.getFullPath("PointsOfImprovement", {
            ":surveyInfoUid": surveyInfoUid,
          })}
        >
          次へ
        </Button>
      </StickyStack>
    </Stack>
  );
}

function Caution() {
  return (
    <Stack sx={{ position: "relative" }}>
      <Stack alignItems="center">
        <Typography
          variant="subtitle1"
          color="#FF3A30"
          sx={{
            position: "absolute",
            backgroundColor: "#FFFFFF",
            px: 2,
            textAlign: "center",
          }}
        >
          注意事項
        </Typography>
      </Stack>
      <Stack
        gap={2}
        sx={{
          border: "solid 1px #FF3A30",
          borderRadius: 2,
          pb: 4,
          pt: 5,
          px: 4,
          mt: "14px",
        }}
      >
        <Typography variant="body2">
          本サービスは疾患の診断、治療、予防を行うものではなく、回答結果を以下のガイドラインや書籍に記載の自己申告内容から照らし合わせた結果を情報提供するものです。
        </Typography>
        <Stack gap={1}>
          <Typography variant="caption" sx={{ lineHeight: "21px", mb: 0 }}>
            参考資料：
          </Typography>
          <List
            sx={{
              listStyleType: "disc",
              ml: "18px",
              py: 0,
              "& .MuiListItem-root": {
                display: "list-item",
                lineHeight: "21px",
                pl: "4px",
                py: 0,
                "&::marker": {
                  color: "text.secondary",
                  content: "'・'",
                  fontSize: "12px",
                  fontWeight: 400,
                },
              },
            }}
          >
            <ListItem>
              <Typography variant="caption">
                睡眠時無呼吸症候群（SAS）の診療ガイドライン 2020
                （日本呼吸器学会）
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="caption">
                臨床睡眠検査マニュアル（日本睡眠学会）
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="caption">
                睡眠障害国際分類
                第3版（米国睡眠医学会（著）日本睡眠学会診断分類委員会（監訳））
              </Typography>
            </ListItem>
          </List>
        </Stack>
      </Stack>
    </Stack>
  );
}

function SASSection({
  loudSnoring,
  sleepyDaytime,
  breathingStopped,
  hypertension,
}: {
  loudSnoring: BasisItem;
  sleepyDaytime: BasisItem;
  breathingStopped: BasisItem;
  hypertension: BasisItem;
}) {
  return (
    <Stack
      sx={{
        backgroundColor: "white",
        border: "solid 1px #E0E0E0",
        borderRadius: 2,
        mx: PX4,
        p: 4,
      }}
    >
      <Typography variant="h6" sx={{ mb: 4 }}>
        睡眠時無呼吸症候群
      </Typography>

      <Stack gap={4} sx={{ mb: 6 }}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          睡眠時無呼吸症候群は、眠っている間に呼吸が止まってしまう病気です。呼吸をするために夜間何度も覚醒してしまい、十分な睡眠がとれなくなります。
        </Typography>
        <ReportTableContainer
          sx={{
            "& .MuiTableHead-root .MuiTableCell-root": {
              width: "156px",
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="caption">よくある自覚症状</Typography>
                </TableCell>
                <TableCell align="right">
                  <YourResult />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  <Typography variant="body2">大きないびきをかく</Typography>
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={loudSnoring.caption}
                    isBad={loudSnoring.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  <Typography variant="body2">日中の疲労感・眠気</Typography>
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={sleepyDaytime.caption}
                    isBad={sleepyDaytime.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  <Typography variant="body2">無呼吸を指摘される</Typography>
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={breathingStopped.caption}
                    isBad={breathingStopped.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  <Typography variant="body2">高血圧である</Typography>
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={hypertension.caption}
                    isBad={hypertension.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
            </TableBody>
          </Table>
        </ReportTableContainer>
        <Typography variant="caption">
          ※アカウント登録時の問診回答内容を基にしているため、前ページの回答記録と異なる結果が表示される場合があります。
        </Typography>
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        リスクが高い人の傾向
      </Typography>

      <Stack gap={2} sx={{ mb: 6 }}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          1. 性別と年齢
          <br />
          この病気は男性に多く、加齢で有病率が上がることが知られています。
        </Typography>
        <TableContainer
          sx={{
            border: "solid 1px rgba(0, 0, 0, 0.12)",
            borderRadius: 0,
            "& .MuiTableCell-root": {
              border: "solid 1px rgba(0, 0, 0, 0.12)",
              p: 2,
              width: "104px",
            },
          }}
        >
          <Table>
            <TableBody>
              <TableRow sx={{ backgroundColor: "#F6F5F4" }}>
                <TableCell></TableCell>
                <TableCell>
                  <RiskByGenderAndAgeText>男性</RiskByGenderAndAgeText>
                </TableCell>
                <TableCell>
                  <RiskByGenderAndAgeText>女性</RiskByGenderAndAgeText>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#F6F5F4" }}>
                  <RiskByGenderAndAgeText>30～40歳程度</RiskByGenderAndAgeText>
                </TableCell>
                <TableCell>
                  <RiskByGenderAndAgeText>10％程度</RiskByGenderAndAgeText>
                </TableCell>
                <TableCell>
                  <RiskByGenderAndAgeText>5％弱</RiskByGenderAndAgeText>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#F6F5F4" }}>
                  <RiskByGenderAndAgeText>50歳代</RiskByGenderAndAgeText>
                </TableCell>
                <TableCell>
                  <RiskByGenderAndAgeText>10～20％程度</RiskByGenderAndAgeText>
                </TableCell>
                <TableCell>
                  <RiskByGenderAndAgeText>10％弱</RiskByGenderAndAgeText>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Stack gap={2}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          2. 肥満
          <br />
          体重が増加することで、喉が閉塞しやすくなりリスクが高まります。
        </Typography>
        <Typography variant="caption">
          ※中等症以上の閉塞性睡眠時無呼吸の約60%は肥満が原因と推定されています。
        </Typography>
      </Stack>
    </Stack>
  );
}

function InsomniaSection({
  aisSleepLatency,
  aisAwakening,
  aisWakeupEarlier,
  aisTotalSleepQuantity,
  aisTotalSleepQuality,
  aisSatisfiedDaily,
  aisDailyActivity,
  aisDailySleepyDaytime,
}: {
  aisSleepLatency: BasisItem;
  aisAwakening: BasisItem;
  aisWakeupEarlier: BasisItem;
  aisTotalSleepQuantity: BasisItem;
  aisTotalSleepQuality: BasisItem;
  aisSatisfiedDaily: BasisItem;
  aisDailyActivity: BasisItem;
  aisDailySleepyDaytime: BasisItem;
}) {
  return (
    <Stack
      sx={{
        backgroundColor: "white",
        border: "solid 1px #E0E0E0",
        borderRadius: 2,
        mx: PX4,
        p: 4,
      }}
    >
      <Typography variant="h6" sx={{ mb: 4 }}>
        不眠症
      </Typography>

      <Stack gap={4} sx={{ mb: 6 }}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          不眠症は、眠りたいのに寝付きが悪い・何度も目が覚める・眠りが浅いなどの睡眠問題があり、結果として日中に不調が発生してしまう病気です。
        </Typography>
        <ReportTableContainer
          sx={{
            "& .MuiTableHead-root .MuiTableCell-root": {
              width: "156px",
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="caption">よくある自覚症状</Typography>
                </TableCell>
                <TableCell align="right">
                  <YourResult />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  寝付きに時間がかかる
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisSleepLatency.caption}
                    isBad={aisSleepLatency.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  睡眠途中に目が覚める
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisAwakening.caption}
                    isBad={aisAwakening.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  起床予定時刻より早く目覚め、それ以上眠れない
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisWakeupEarlier.caption}
                    isBad={aisWakeupEarlier.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  総睡眠時間が足りない
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisTotalSleepQuantity.caption}
                    isBad={aisTotalSleepQuantity.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  睡眠の質への不満
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisTotalSleepQuality.caption}
                    isBad={aisTotalSleepQuality.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  日中の満足度が低い
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisSatisfiedDaily.caption}
                    isBad={aisSatisfiedDaily.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  日中の身体／精神活動の低下
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisDailyActivity.caption}
                    isBad={aisDailyActivity.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
              <TableRow>
                <SleepDisorderTableBodyCell>
                  日中の強い眠気
                </SleepDisorderTableBodyCell>
                <SleepDisorderTableBodyCell align="right">
                  <BasisText
                    caption={aisDailySleepyDaytime.caption}
                    isBad={aisDailySleepyDaytime.isBad}
                  />
                </SleepDisorderTableBodyCell>
              </TableRow>
            </TableBody>
          </Table>
        </ReportTableContainer>
        <Typography variant="caption">
          ※アカウント登録時の問診回答内容を基にしているため、前ページの回答記録と異なる結果が表示される場合があります。
        </Typography>
      </Stack>
    </Stack>
  );
}

function YourResult() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      whiteSpace="nowrap"
    >
      <Typography variant="caption">あなたの結果</Typography>
      <Typography
        sx={{
          fontSize: "62.5%",
          position: "relative",
          top: "-2.5px",
        }}
      >
        ※
      </Typography>
    </Stack>
  );
}

function SleepDisorderTableBodyCell(props: TableCellProps) {
  const { sx, ...rest } = props;
  return <TableCell sx={{ px: 2, py: 3, ...sx }} {...rest} />;
}

function RiskByGenderAndAgeText({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        color: "text.primary",
        fontSize: "12px",
        fontWeight: 400,
      }}
    >
      {children}
    </Typography>
  );
}

function BasisText({ caption, isBad }: { caption?: string; isBad?: boolean }) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: isBad ? "error.main" : "text.disabled",
      }}
    >
      {caption}
    </Typography>
  );
}
